import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { updateShot } from "@/lib/api";
import { toast } from "sonner";

const CAMERA_ANGLES = [
  "Wide shot",
  "Medium shot",
  "Close-up",
  "Over-the-shoulder",
  "First-person POV",
  "Low angle",
  "High angle",
  "Tracking shot",
];

export default function ShotEditDialog({ shot, open, onOpenChange, onSaved }) {
  const [form, setForm] = useState(shot);
  const [saving, setSaving] = useState(false);

  React.useEffect(() => {
    setForm(shot);
  }, [shot]);

  if (!form) return null;

  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const handleSave = async () => {
    setSaving(true);
    try {
      const updated = await updateShot(form.id, {
        characters: form.characters,
        outfit_refs: form.outfit_refs,
        location_ref: form.location_ref || null,
        prop_refs: form.prop_refs,
        setting: form.setting,
        pose_action: form.pose_action,
        camera_angle: form.camera_angle,
        is_pov: form.is_pov,
        motion_description: form.motion_description,
        output_type: form.output_type,
        mood_sfx: form.mood_sfx,
        text_in_post: form.text_in_post,
      });
      toast.success(`Shot S${String(updated.scene_number).padStart(2, "0")}_SH${String(updated.shot_number).padStart(2, "0")} updated`);
      onSaved(updated);
      onOpenChange(false);
    } catch (e) {
      toast.error("Failed to update shot");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-slate-900 border-slate-800 max-w-xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading uppercase tracking-wider">
            Edit Shot S{String(form.scene_number).padStart(2, "0")}_SH{String(form.shot_number).padStart(2, "0")}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label className="text-xs text-slate-400 font-mono uppercase tracking-wider">Characters (comma separated)</Label>
            <Input
              data-testid="shot-edit-characters-input"
              value={(form.characters || []).join(", ")}
              onChange={(e) => set("characters", e.target.value.split(",").map((s) => s.trim()).filter(Boolean))}
              className="bg-slate-950 border-slate-800 rounded-sm"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="text-xs text-slate-400 font-mono uppercase tracking-wider">Location Lock (continuity)</Label>
              <Input
                data-testid="shot-edit-location-ref-input"
                value={form.location_ref || ""}
                onChange={(e) => set("location_ref", e.target.value)}
                placeholder="e.g. Blockbuster Interior (Intact)"
                className="bg-slate-950 border-slate-800 rounded-sm"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-slate-400 font-mono uppercase tracking-wider">Prop Locks (comma separated)</Label>
              <Input
                data-testid="shot-edit-prop-refs-input"
                value={(form.prop_refs || []).join(", ")}
                onChange={(e) => set("prop_refs", e.target.value.split(",").map((s) => s.trim()).filter(Boolean))}
                className="bg-slate-950 border-slate-800 rounded-sm"
              />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs text-slate-400 font-mono uppercase tracking-wider">Setting</Label>
            <Input
              data-testid="shot-edit-setting-input"
              value={form.setting}
              onChange={(e) => set("setting", e.target.value)}
              className="bg-slate-950 border-slate-800 rounded-sm"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs text-slate-400 font-mono uppercase tracking-wider">Pose / Action</Label>
            <Textarea
              data-testid="shot-edit-action-input"
              value={form.pose_action}
              onChange={(e) => set("pose_action", e.target.value)}
              rows={2}
              className="bg-slate-950 border-slate-800 rounded-sm"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="text-xs text-slate-400 font-mono uppercase tracking-wider">Camera Angle</Label>
              <Select value={form.camera_angle} onValueChange={(v) => set("camera_angle", v)}>
                <SelectTrigger data-testid="shot-edit-camera-select" className="bg-slate-950 border-slate-800 rounded-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-800">
                  {CAMERA_ANGLES.map((a) => (
                    <SelectItem key={a} value={a}>{a}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-slate-400 font-mono uppercase tracking-wider">Output Type</Label>
              <Select value={form.output_type} onValueChange={(v) => set("output_type", v)}>
                <SelectTrigger data-testid="shot-edit-output-select" className="bg-slate-950 border-slate-800 rounded-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-800">
                  <SelectItem value="still">Still</SelectItem>
                  <SelectItem value="clip">Clip (5-10s)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Checkbox
              data-testid="shot-edit-pov-checkbox"
              checked={form.is_pov}
              onCheckedChange={(v) => set("is_pov", !!v)}
            />
            <Label className="text-sm text-slate-300">First-person Viewer POV shot</Label>
          </div>
          <div className="flex items-center gap-2">
            <Checkbox
              data-testid="shot-edit-text-in-post-checkbox"
              checked={form.text_in_post}
              onCheckedChange={(v) => set("text_in_post", !!v)}
            />
            <Label className="text-sm text-slate-300">Text in post — on-screen text is illegible/blurred, composited in DaVinci later</Label>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs text-slate-400 font-mono uppercase tracking-wider">Motion Description</Label>
            <Input
              data-testid="shot-edit-motion-input"
              value={form.motion_description}
              onChange={(e) => set("motion_description", e.target.value)}
              className="bg-slate-950 border-slate-800 rounded-sm"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs text-slate-400 font-mono uppercase tracking-wider">Mood / SFX Context</Label>
            <Input
              data-testid="shot-edit-mood-input"
              value={form.mood_sfx}
              onChange={(e) => set("mood_sfx", e.target.value)}
              className="bg-slate-950 border-slate-800 rounded-sm"
            />
          </div>
        </div>
        <DialogFooter>
          <Button
            data-testid="shot-edit-save-btn"
            onClick={handleSave}
            disabled={saving}
            className="bg-blue-600 hover:bg-blue-500 text-white rounded-sm w-full"
          >
            {saving ? "Saving…" : "Save Shot"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
